import { normalizeParams, Param } from '@/lib/query/normalizeParams';
import { queryClient } from '@/lib/query/queryClient';

type FetcherParams = Record<string, Param>;

/** Builds a stable TanStack Query cache key array for a Hypertracker API path and optional params. */
export function makeApiKey(apiPath: string, params?: FetcherParams) {
    return params
        ? (['fetcher', 'api', apiPath, normalizeParams(params)] as const)
        : (['fetcher', 'api', apiPath] as const);
}

/** Runs an updater function on a specific TanStack Query cache entry for optimistic mutation updates. */
export function updateApiCache<T>(
    apiPath: string,
    params: FetcherParams | undefined,
    updater: (old: T | undefined) => T | undefined,
) {
    queryClient.setQueryData<T>(makeApiKey(apiPath, params), updater);
}

/** Runs an updater on all cache entries sharing the same apiPath, regardless of params. */
export function updateAllApiCachesForPath<T>(
    apiPath: string,
    updater: (old: T | undefined) => T | undefined,
) {
    queryClient.setQueriesData(
        {
            predicate(q) {
                const queryKey = q.queryKey as any[];
                return (
                    queryKey[0] === 'fetcher' && queryKey[1] === 'api' && queryKey[2] === apiPath
                );
            },
        },
        updater as any,
    );
}

/** Cancels any in-flight TanStack Query request for the given API path and params. */
export async function cancelApiQuery(apiPath: string, params?: FetcherParams) {
    await queryClient.cancelQueries({ queryKey: makeApiKey(apiPath, params) });
}

/** Marks a specific API cache entry stale and triggers a refetch for active queries. */
export function invalidateFetcherApiByPath(apiPath: string, params?: FetcherParams) {
    queryClient.invalidateQueries({
        queryKey: makeApiKey(apiPath, params),
        refetchType: 'active',
    });
}

/** Marks all cache entries for an API path stale and triggers refetches, ignoring param differences. */
export function invalidateAllFetcherApiByPath(apiPath: string) {
    queryClient.invalidateQueries({
        predicate(q) {
            const queryKey = q.queryKey as any[];
            return queryKey[0] === 'fetcher' && queryKey[1] === 'api' && queryKey[2] === apiPath;
        },
        refetchType: 'active',
    });
}
